import { BASIC_MEMBER_TIME_METRICS_FRAGMENT } from 'apollo/queries/jit-member-queries';
import { IDashboardSettingsItem } from 'components/domain/dashboard/DashboardSettingsItem/DashboardSettingsItem';
import {
  getDefaultDashboardListRow,
  IDashboardListRow,
} from 'components/foundation/dashboard/dashboard-list/DashboardList/DashboardList';
import {
  MEMBERS_WITH_COST_CODE_TIME_ONLY_METRIC_QUERY,
  MEMBERS_WITH_EQUIPMENT_TIME_ONLY_METRIC_QUERY,
  MEMBERS_WITH_PROJECT_TIME_ONLY_METRIC_QUERY,
  MEMBERS_WITH_TIME_ONLY_METRIC_QUERY,
} from 'containers/activity-reports/queries/member-activity-queries';
import { useDashboardTimeRange } from 'containers/dashboard/hooks';
import { getDashboardTimeFrameTitle } from 'containers/dashboard/util/utils';
import gql from 'graphql-tag';
import { useApolloPaging, useAsync } from 'hooks';
import { first, isNil, take } from 'lodash';
import { Duration } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import ICursorable from 'types/Cursorable';
import MemberPermission from 'types/enum/MemberPermission';
import IMember from 'types/Member';
import { mapNotNull, takeWithPopulate } from 'utils/collectionUtils';
import { getMetricLaborTotals } from 'utils/jitMetricUtils';
import { getFullNameFromMember } from 'utils/stringUtils';
import { sortByField } from 'utils/tableUtils';
import { LaborMetricsInterval } from '__generated__/graphql';

export const ACTIVE_MEMBERS_QUERY = gql`
  query ActiveMembersQuery(
    $first: Int
    $after: String
    $filter: MemberFilter
    $metricsStartDate: Date!
    $metricsEndDate: Date!
  ) {
    members(first: $first, after: $after, filter: $filter, sort: [{ firstName: asc }, { lastName: asc }]) {
      cursor
      id
      firstName
      lastName
      memberLaborMetrics(interval: custom, start: $metricsStartDate, end: $metricsEndDate) {
        ...BasicMemberLaborMetrics
        __typename
      }
    }
  }
  ${BASIC_MEMBER_TIME_METRICS_FRAGMENT}
`;

export default function useActiveMembersDashboard(
  setting: IDashboardSettingsItem,
  projectId?: string | null,
  costCodeId?: string | null,
  equipmentId?: string | null
) {
  const { getAll } = useApolloPaging();
  const timeFrame = setting.options?.time ?? 'pastFourteen';
  const timeRange = useDashboardTimeRange(timeFrame);

  const getData = useCallback(() => {
    let query = MEMBERS_WITH_TIME_ONLY_METRIC_QUERY;
    if (!isNil(costCodeId)) {
      query = MEMBERS_WITH_COST_CODE_TIME_ONLY_METRIC_QUERY;
    } else if (!isNil(projectId)) {
      query = MEMBERS_WITH_PROJECT_TIME_ONLY_METRIC_QUERY;
    } else if (!isNil(equipmentId)) {
      query = MEMBERS_WITH_EQUIPMENT_TIME_ONLY_METRIC_QUERY;
    }

    return getAll<IMember & ICursorable>('members', {
      query: query,
      fetchPolicy: 'network-only',
      variables: {
        first: 200,
        sort: [{ firstName: 'asc' }, { lastName: 'asc' }],
        filter: {
          permissions: {
            permissions: MemberPermission.TIME_EVENTS,
            operationType: 'and',
          },
          archivedOn: { isNull: true },
          hasTime: {
            startTime: timeRange.startTime.toISO({
              suppressMilliseconds: true,
              includeOffset: false,
            }),
            endTime: timeRange.endTime.toISO({
              suppressMilliseconds: true,
              includeOffset: false,
            }),
            includeOpenEntry: false,
            paidTimeOff: null,
            costCodeId: !isNil(costCodeId) ? { equal: costCodeId } : undefined,
            projectIdWithDescendants: !isNil(projectId) ? { equal: projectId } : undefined,
            equipmentId: !isNil(equipmentId) ? { equal: equipmentId } : undefined,
          },
        },
        metricsStartDate: timeRange.startTime.toISODate(),
        metricsEndDate: timeRange.endTime.toISODate(),
        metricsInterval: LaborMetricsInterval.Custom,
        costCodeIds: !isNil(costCodeId) ? [costCodeId] : undefined,
        projectIds: !isNil(projectId) ? [projectId] : undefined,
        equipmentIds: !isNil(equipmentId) ? [equipmentId] : undefined,
      },
    });
  }, [costCodeId, equipmentId, getAll, projectId, timeRange.endTime, timeRange.startTime]);

  const { execute, loading, error, data } = useAsync(getData, false);
  const rowData: IDashboardListRow[] = useMemo(() => {
    const titleToTotal = mapNotNull(data ?? [], (member) => {
      // do not include pto
      let metrics = getMetricLaborTotals(first(member.memberLaborMetrics));

      if (!isNil(costCodeId)) {
        metrics = getMetricLaborTotals(first(member.memberCostCodeLaborMetrics));
      } else if (!isNil(projectId)) {
        metrics = getMetricLaborTotals(first(member.memberProjectLaborMetrics));
      } else if (!isNil(equipmentId)) {
        metrics = getMetricLaborTotals(first(member.memberEquipmentLaborMetrics));
      }

      const total = metrics?.totalSeconds ?? 0;
      const pto = metrics?.paidTimeOffSeconds ?? 0;
      const activeTotal = total - pto;

      return activeTotal > 0
        ? {
            id: member.id,
            title: getFullNameFromMember(member),
            total: activeTotal,
          }
        : null;
    });

    const activeProjects = take(sortByField(titleToTotal, 'total', 'DESC'), 5).map((item) => {
      return {
        value: Duration.fromMillis(item.total * 1000).toFormat('h:mm'),
        label: item.title,
        id: item.id,
      };
    });

    return takeWithPopulate(activeProjects, 5, getDefaultDashboardListRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    execute();
  }, [execute, timeFrame, costCodeId, projectId, equipmentId]);

  const title = getDashboardTimeFrameTitle(setting.title, timeFrame);
  return { data: rowData, title, setting, loading, error, execute };
}
